import { BookingState } from '../../modules/booking-state/BookingState';
import { Booking, BookingsRequest, CancelBookingRequest, Dealer } from '../../modules/entities/Entities';
import { BookingsPageContext } from './BookingsPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import React, { useState } from 'react';
import { useModalManager } from '../../modules/contexts/modal-manager-context/ModalManagerProvider';
import { useAmendNavigate } from './UseAmendNavigate';
import { useBrandNavigate } from '../../modules/navigation/UseBrandNavigate';
import { CancelConfirmationModalContent } from './cancel-confirmation-modal-content/CancelConfirmationModalContent';
import { PageRoutes } from '../../modules/navigation/PageRoutes';
import { useCancelBookingRequest, useGetBookings, useGetDealers } from '../../modules/booking-api/BookingApi';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { BookingsPageViewProps } from './BookingsPageViewProps';

function createCancelBookingRequest(booking: Booking): CancelBookingRequest {
  return {
    referenceNumber: booking.bookingRef,
  };
}

function ContextAccessor({ nationalIdNumber, passportNumber, vehicleDetails }: BookingState): BookingsPageContext | null {
  if (!((nationalIdNumber || passportNumber) && vehicleDetails)) {
    return null;
  }

  return {
    nationalIdNumber,
    passportNumber,
    vehicleDetails,
  };
}

export const BookingsPage = withPageContext(ContextAccessor, ({ pageContext }) => {
  const viewProvider = useViewProvider();
  const [bookingState, setBookingState] = useBookingContext();
  const [getBookingsRequest] = useState<BookingsRequest>(() => ({
    customerId: pageContext.nationalIdNumber ?? '',
    passportNumber: pageContext.passportNumber ?? '',
    vin: pageContext.vehicleDetails.vin,
  }));
  const modalManager = useModalManager();
  const amendNavigate = useAmendNavigate([bookingState, setBookingState]);
  const navigate = useBrandNavigate();

  const { data: bookings, refetch: refetchBookings } = useGetBookings(getBookingsRequest);
  const { data: dealers } = useGetDealers(['V', 'C']);
  const { mutate: sendCancelBookingRequest } = useCancelBookingRequest({
    onSuccess: () => refetchBookings(),
  });

  const onAmendBooking = (booking: Booking, dealer: Dealer): void => {
    amendNavigate(booking, dealer);
  };
  const onCancelBooking = (booking: Booking): void => {
    const onConfirmClick = (onClose: () => void): void => {
      sendCancelBookingRequest(createCancelBookingRequest(booking));
      onClose();
    };
    const onCancelClick = (onClose: () => void): void => {
      onClose();
    };

    modalManager.open({
      title: 'Cancel booking confirmation',
      content: () => <CancelConfirmationModalContent booking={booking} />,
      actions: [
        {
          title: 'Confirm',
          type: 'primary',
          onClick: onConfirmClick,
          key: 'confirm',
        },
        {
          title: 'Cancel',
          type: 'secondary',
          onClick: onCancelClick,
          key: 'cancel',
        },
      ],
    });
  };
  const onBookNewService = (): void => {
    navigate(PageRoutes.VehicleDetails);
  };

  const viewProps: BookingsPageViewProps = {
    vehicleDetails: pageContext.vehicleDetails,
    bookings,
    dealers,
    onAmendBooking,
    onCancelBooking,
    onBookNewService,
  };

  return viewProvider.renderBookingsPage(viewProps);
});
